import * as p from '@clack/prompts'
import path from 'node:path'
import type { LoadedAuthoredGeneratedSchema } from '@cli-types/core-engine/add-field.js'
import type { AddFieldCommandOptions } from '@cli-types/core-engine/commands.js'
import type { PreparedFieldInsertion } from '@cli-types/next-adapter/commands.js'
import { UNSUPPORTED_PROJECT_STATE_MESSAGE } from '@cli-constants/snapshots.js'
import { cancelAndExit, isInteractiveSession } from '@cli-utils/terminal.js'
import { insertFieldIntoTarget } from '@core-engine/schema/add-field/insert-field-into-target.js'
import { isRequiredPersistedSchemaField } from '@core-engine/schema/add-field/is-required-persisted-schema-field.js'
import { loadAuthoredGeneratedSchema } from '@core-engine/schema/add-field/load-authored-generated-schema.js'
import { validateAuthoredGeneratedSchema } from '@core-engine/schema/add-field/validate-authored-generated-schema.js'
import { writeAuthoredGeneratedSchema } from '@core-engine/schema/add-field/write-authored-generated-schema.js'
import { loadManifest } from '@core-engine/snapshots/store/load-manifest.js'
import { snapshotRootExists } from '@core-engine/snapshots/store/snapshot-root-exists.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { resolveConfigOrExit } from '@next-adapter/config/resolve-config-or-exit.js'
import { runGenerateCommand } from '../generate/run-generate-command.js'
import { fail } from '../schema-prompts/fail.js'
import { collectRelationshipTargetWarnings } from './collect-relationship-target-warnings.js'
import { confirmWarningCases } from './confirm-warning-cases.js'
import { createInteractiveInsertion } from './create-interactive-insertion.js'
import { createNonInteractiveInsertion } from './create-non-interactive-insertion.js'
import { hasNonInteractiveFieldOptions } from './has-non-interactive-field-options.js'
import { printPreview } from './print-preview.js'
import { promptConfirm } from './prompt-confirm.js'
import { resolveSchemaNameInteractively } from './resolve-schema-name-interactively.js'
import { resolveSchemaOwner } from './resolve-schema-owner.js'
import { schemaNameFromLoaded } from './schema-name-from-loaded.js'

export async function runAddFieldCommand(
  schemaName: string | undefined,
  options: AddFieldCommandOptions
): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const nonInteractive = hasNonInteractiveFieldOptions(options)

  if (!snapshotRootExists(cwd)) {
    fail(UNSUPPORTED_PROJECT_STATE_MESSAGE)
  }

  const config = await resolveConfigOrExit(cwd)
  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))
  const selectedSchemaName = await resolveSchemaNameInteractively(schemasDir, schemaName)

  let loaded: LoadedAuthoredGeneratedSchema
  try {
    loaded = loadAuthoredGeneratedSchema(schemasDir, selectedSchemaName)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  if (!nonInteractive && !isInteractiveSession()) {
    fail('Interactive add-field requires a TTY. Provide --type, --field, and --label instead.')
  }

  const owner = resolveSchemaOwner(cwd, schemaNameFromLoaded(loaded))
  const firstTimeGeneration = !loadManifest(cwd, schemaNameFromLoaded(loaded))
  let insertion: PreparedFieldInsertion
  try {
    insertion = nonInteractive
      ? createNonInteractiveInsertion(loaded, schemasDir, options)
      : await createInteractiveInsertion(loaded, schemasDir)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  const requiredPersistedField = isRequiredPersistedSchemaField(
    loaded,
    insertion.field,
    insertion.target
  )

  await confirmWarningCases(
    owner,
    firstTimeGeneration,
    requiredPersistedField,
    nonInteractive,
    options.yes,
    collectRelationshipTargetWarnings(cwd, owner, insertion.field)
  )

  try {
    insertFieldIntoTarget(insertion.target, insertion.field, insertion.order)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  const validationErrors = validateAuthoredGeneratedSchema(loaded)
  if (validationErrors.length > 0) {
    fail(
      `Schema validation failed after insertion:\n${validationErrors.map((error) => `  - ${error}`).join('\n')}`
    )
  }

  printPreview(loaded, owner, firstTimeGeneration, insertion, options)

  if (!nonInteractive && !options.yes) {
    const confirmed = await promptConfirm('Write schema JSON and regenerate this schema?', true)
    if (!confirmed) {
      cancelAndExit('Add field cancelled.')
    }
  }

  writeAuthoredGeneratedSchema(loaded)
  p.log.success(`Updated ${path.relative(cwd, loaded.filePath)}`)

  try {
    await runGenerateCommand(selectedSchemaName, {
      force: false,
      acceptGenerated: Boolean(options.acceptGenerated || options.yes),
      all: false,
      interactive: true,
      skipMigration: Boolean(options.skipMigration),
      cwd
    })
  } catch (error) {
    p.log.error(error instanceof Error ? error.message : String(error))
    p.log.info(
      `Schema JSON was kept. Re-run \`betterstart generate ${selectedSchemaName}${options.skipMigration ? ' --skip-migration' : ''}\` after resolving the issue.`
    )
    process.exit(1)
  }
}
